/**
 *  @class
 *  @function ProductRecommendations
 *  Fetches its recommendations once per element. When none come back, placeholder cards are removed and the
 *  section steps out of the page, except in the theme editor, where it stays so the merchant can select it.
 */
if (!customElements.get('product-recommendations')) {
	class ProductRecommendations extends HTMLElement {
		connectedCallback() {
			if (this.requested || !this.dataset.url) return;
			this.requested = true;
			this.parent = this.closest('.product-recommendations--parent');
			this.fetchProducts();
		}
		fetchProducts() {
			fetch(this.dataset.url)
				.then((response) => {
					if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);
					return response.text();
				})
				.then((text) => {
					const html = document.createElement('div');
					html.innerHTML = text;
					const recommendations = html.querySelector('product-recommendations');
					let hasProducts = Boolean(recommendations && recommendations.innerHTML.trim().length);
					// Same world only (data-world, set by the section): products from other worlds are dropped.
					// Adult products (data-world="adults" on the card) are always dropped unless the source is adult.
					// Other styles lead; the source's own colour group (data-siblings, already its colour dots) only fills up.
					if (hasProducts) {
						hasProducts = this.filterWorlds(recommendations) > 0;
					}

					if (hasProducts) {
						this.innerHTML = recommendations.innerHTML;

						if (this.parent) {
							this.parent.classList.add('product-recommendations--full');

							if (typeof OverlayManager !== 'undefined' && OverlayManager.isOpen(document.getElementById('Cart-Drawer'))) {
								this.parent.classList.add('active');
							}
						}
					} else {
						this.clearPlaceholders();
					}

					this.classList.add('product-recommendations--loaded');
				})
				.catch((error) => {
					this.clearPlaceholders();
					console.error(error);
				});
		}
		// Keeps the allowed cards, other styles first, up to data-limit; returns how many remain. Responses whose
		// cards carry no data-world (other markup) are left as they are. Cards of the source's colour group (its other
		// colours) go after the other styles, so they only fill the row when a world has few other pieces.
		filterWorlds(container) {
			const items = container.querySelectorAll('[data-world]');
			if (!items.length) return 1;
			const limit = parseInt(this.dataset.limit, 10) || Infinity;
			const { world, sourceWorld, siblings } = this.dataset;
			const adultSource = (world || sourceWorld) === 'adults';
			const allowed = [...items].filter((item) => {
				const ok = world ? item.dataset.world === world : adultSource || item.dataset.world !== 'adults';
				if (!ok) item.remove();
				return ok;
			});
			const sameStyle = (item) => Boolean(siblings) && item.dataset.siblings === siblings;
			const ordered = [...allowed.filter((item) => !sameStyle(item)), ...allowed.filter(sameStyle)];
			ordered.forEach((item, index) => {
				if (index < limit) item.parentElement.appendChild(item);
				else item.remove();
			});
			return Math.min(ordered.length, limit);
		}
		// No recommendations came back: a section that was holding its place with placeholder cards steps out.
		clearPlaceholders() {
			if (!this.querySelector('.product-card-placeholder')) return;
			this.replaceChildren();
			if (window.Shopify && window.Shopify.designMode) return;
			const section = this.closest('.shopify-section');
			if (section) section.hidden = true;
		}
	}

	customElements.define('product-recommendations', ProductRecommendations);
}
